import { isDotCom } from '@src/site';
import { ThemeIcon } from '@src/theme/ThemeIcon';
import { SvgArtwork } from '@src/theme/SvgArtwork';
import participantsIcon from './assets/social-proof/participants.svg?raw';
import gamesIcon from './assets/social-proof/games.svg?raw';
import returningIcon from './assets/social-proof/returning.svg?raw';
import { useTranslation } from 'react-i18next';
import { Panel } from '@src/components/Panel/Panel';
import { Typography } from '@src/components/Typography/Typography';
import { LandingSection } from '@src/components/LandingLayout/LandingLayout';
import { useTheme } from '@src/theme/useTheme';
import './HomeSocialProof.css';

// Verified 2025 totals. Organizer figures include only games with an active draw.
export const HOME_SEASON_STATS = {
  year: 2025,
  santas: 2_470_756,
  games: 230_364,
  returningOrganizers: 62_698,
  totalOrganizers: 181_358,
};

export function HomeSocialProof() {
  const { t, i18n } = useTranslation();
  const stats = HOME_SEASON_STATS;
  const copyKey = isDotCom() ? 'home.socialProofCom' : 'home.socialProof';
  const { season } = useTheme();
  const metrics = [
    {
      key: 'santas',
      compact: 2.4,
      unit: 'million',
      icon: participantsIcon,
      themeIcon: 'social-participants' as const,
      decoration: 9 as const,
    },
    {
      key: 'games',
      compact: 230,
      unit: 'thousand',
      icon: gamesIcon,
      themeIcon: 'social-games' as const,
      decoration: 10 as const,
    },
    {
      key: 'returning',
      compact: 62,
      unit: 'thousand',
      icon: returningIcon,
      themeIcon: 'social-returning' as const,
      decoration: 7 as const,
    },
  ];
  return (
    <LandingSection variant="ground" gap={8} align="center" wide testId="home-social-proof">
      <div className="home-social-proof__heading">
        <span className="home-social-proof__eyebrow">{t(`${copyKey}.eyebrow`)}</span>
        <Typography as="h2" role="title/section">
          {t(`${copyKey}.title`, { year: stats.year })}
        </Typography>
        <Typography as="p" role="body/lead">
          {t(`${copyKey}.lead`)}
        </Typography>
      </div>
      <div className="home-social-proof__grid">
        {metrics.map(({ key, compact, unit, decoration, icon, themeIcon }, index) => (
          <Panel
            key={key}
            as="article"
            padding="none"
            elevated
            edges={
              season === 'egypt'
                ? undefined
                : {
                    pieces:
                      season === 'winter'
                        ? decoration
                        : season === 'pixel'
                          ? ([1, 5, 6] as const)[index]
                          : ([1, 2, 3] as const)[index],
                  }
            }
            className="home-social-proof__card"
          >
            <span
              className={`home-social-proof__icon home-social-proof__icon--${key}`}
              aria-hidden="true"
            >
              <ThemeIcon name={themeIcon} fallback={<SvgArtwork svg={icon} />} />
            </span>
            <dl>
              <dt>{t(`${copyKey}.${key}`, { year: stats.year })}</dt>
              <dd>
                {key === 'returning'
                  ? t(`${copyKey}.returningHeadline`)
                  : t(`${copyKey}.${unit}`, {
                      value: new Intl.NumberFormat(i18n.language).format(compact),
                    })}
              </dd>
            </dl>
          </Panel>
        ))}
      </div>
      <p className="home-social-proof__source">
        {t(`${copyKey}.source`, {
          year: stats.year,
          santas: new Intl.NumberFormat(i18n.language).format(stats.santas),
          games: new Intl.NumberFormat(i18n.language).format(stats.games),
          returning: new Intl.NumberFormat(i18n.language).format(stats.returningOrganizers),
          total: new Intl.NumberFormat(i18n.language).format(stats.totalOrganizers),
        })}
      </p>
    </LandingSection>
  );
}
